import React, { useEffect, useState } from 'react';
import stockService from '../services/stockService';
import { StockItemResponseDTO, StockMovementRequestDTO, StockMovementResponseDTO } from '../types/stock';
import { PageResponse } from '../types/api';
import alertService from '../services/alertService';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import StockMovementForm from '../components/stock/StockMovementForm';
import { History, Plus, ArrowLeftRight, ArrowLeft } from 'lucide-react';

const StockMovementPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Estados de formulário removidos: uso exclusivo via StockMovementForm no modal

  // Listagem de Movimentações
  const [listLoading, setListLoading] = useState(false);
  const [listData, setListData] = useState<PageResponse<StockMovementResponseDTO> | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [filterProdutoId, setFilterProdutoId] = useState('');
  const [filterLocalId, setFilterLocalId] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [movementTypes, setMovementTypes] = useState<string[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const { id: produtoIdParam } = useParams<{ id?: string }>();
  const [filtersReady, setFiltersReady] = useState(false);
  const [stockSummary, setStockSummary] = useState<StockItemResponseDTO | null>(null);
  const [summaryReloadFlag, setSummaryReloadFlag] = useState(0);

  // Removido: ajuste de locais conforme tipo (tratado no formulário do modal)

  // Ler parâmetros da URL para filtros iniciais (path e query)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Preferir id do path /estoque/movimentacoes/produto/:id
    setFilterProdutoId(produtoIdParam ?? params.get('idProduto') ?? '');
    setFilterLocalId(params.get('idLocal') ?? '');
    setFilterTipo(params.get('tipoMovimentacao') ?? '');
    setFiltersReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produtoIdParam]);

  // Carregar resumo de estoque para o card superior
  useEffect(() => {
    const loadStockSummary = async () => {
      if (!filtersReady || !filterProdutoId) { setStockSummary(null); return; }
      try {
        const res = await stockService.getByProduct(filterProdutoId, filterLocalId || undefined);
        if (res.success) {
          const items = (res.data?.content ?? []).filter((it) => it.produtoId === filterProdutoId);
          if ((filterLocalId || '').length > 0) {
            const item = items.find((it) => it.localId === filterLocalId) ?? items[0] ?? null;
            setStockSummary(item);
          } else if (items.length > 0) {
            const totalSaldo = items.reduce((acc, it) => acc + (it.quantidadeAtual ?? 0), 0);
            const totalReservado = items.reduce((acc, it) => acc + (it.reservado ?? 0), 0);
            const first = items[0];
            setStockSummary({
              produtoId: filterProdutoId,
              produtoCodigo: first.produtoCodigo,
              produtoNome: first.produtoNome ?? first.produtoId,
              quantidadeAtual: totalSaldo,
              reservado: totalReservado,
              // Sem local específico
              localId: undefined,
              localNome: undefined,
              minimo: undefined,
              maximo: undefined,
              ativo: first.ativo,
              updatedAt: first.updatedAt,
            });
          } else {
            setStockSummary(null);
          }
        } else {
          setStockSummary(null);
        }
      } catch {
        setStockSummary(null);
      }
    };
    loadStockSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, filterProdutoId, filterLocalId, summaryReloadFlag]);

  // Carrega tipos de movimentação do estoque
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const resp = await stockService.getMovementTypes();
        setMovementTypes(resp.data ?? []);
      } catch {
        setMovementTypes([]);
      }
    };
    loadTypes();
  }, []);

  // Helper para exibir rótulos amigáveis
  const labelForType = (code: string) => {
    switch (code) {
      case 'ENTRADA': return 'Entrada';
      case 'SAIDA': return 'Saída';
      case 'DEVOLUCAO': return 'Devolução';
      case 'AJUSTE': return 'Ajuste';
      case 'TRANSFERENCIA_ENTRADA': return 'Transferência Entrada';
      case 'TRANSFERENCIA_SAIDA': return 'Transferência Saída';
      case 'PERDA': return 'Perda';
      case 'INVENTARIO': return 'Inventário';
      default: return code;
    }
  };

  // Formatação de data/hora
  const formatDateTime = (iso?: string) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '-';
      return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(d);
    } catch {
      return '-';
    }
  };

  const loadMovements = async () => {
    try {
      setListLoading(true);
      const res = await stockService.listMovements({
        page,
        size,
        produtoId: filterProdutoId || undefined,
        localId: filterLocalId || undefined,
        tipoMovimentacao: filterTipo || undefined,
      });
      if (res.success) setListData(res.data);
      else alertService.error('Falha ao carregar movimentações');
    } catch (err) {
      alertService.error('Erro ao carregar movimentações');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (!filtersReady) return;
    loadMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, page, size, filterProdutoId, filterLocalId, filterTipo]);

  // Removido: submissão local. Registro ocorre via StockMovementForm dentro do modal

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const targetId = (filterProdutoId || '').trim();
              if (targetId) {
                navigate(`/produtos/${encodeURIComponent(targetId)}/visualizar`);
              } else {
                navigate('/produtos');
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <History className="h-6 w-6 text-red-600" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Movimentações</h1>
        </div>
        <div>
          <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2" onClick={() => setOpenModal(true)}>
            <Plus className="h-4 w-4" />
            Registrar Movimentação
          </button>
        </div>
  </div>
  {/* Formulário removido: uso via modal acionado pelo botão acima */}

  {/* Card de Resumo de Estoque/Produto */}
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    {stockSummary ? (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="text-xs text-gray-500">Produto</div>
          <div className="text-sm font-medium text-gray-900">{stockSummary.produtoNome ?? stockSummary.produtoId}</div>
          <div className="text-xs text-gray-500 mt-1">Código: {stockSummary.produtoCodigo ?? '-'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Local</div>
          <div className="text-sm font-medium text-gray-900">{stockSummary.localNome ?? stockSummary.localId ?? '-'}</div>
          <div className="text-xs text-gray-500 mt-1">ID: {stockSummary.localId ?? '-'}</div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-xs text-gray-500">Disponível</div>
            <div className="text-sm font-semibold text-gray-900">{stockSummary.quantidadeAtual}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Reservado</div>
            <div className="text-sm font-semibold text-gray-900">{stockSummary.reservado ?? 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Mín/Máx</div>
            <div className="text-sm font-semibold text-gray-900">{stockSummary.minimo ?? '-'} / {stockSummary.maximo ?? '-'}</div>
          </div>
        </div>
      </div>
    ) : (
      <div className="text-sm text-gray-600">Carregando resumo do estoque...</div>
    )}
  </div>

  {/* Filtros de Listagem (produto/local travados) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} className="border rounded px-2 py-2">
              <option value="">Todos</option>
              {movementTypes.map((t) => (
                <option key={t} value={t}>{labelForType(t)}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button type="button" onClick={() => { setPage(0); loadMovements(); }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Buscar</button>
          </div>
        </div>
      </div>

      {/* Tabela de Movimentações */}
      <div className="bg-white rounded border overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantidade</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Origem</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Destino</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {listLoading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Carregando...</td></tr>
            )}
            {!listLoading && (listData?.content?.length ?? 0) === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Nenhuma movimentação encontrada</td></tr>
            )}
            {!listLoading && listData?.content?.map((mv) => (
              <tr key={mv.id}>
                <td className="px-4 py-2 text-sm">{mv.produtoNome ?? mv.produtoId}</td>
                <td className="px-4 py-2 text-sm">{mv.tipo}</td>
                <td className="px-4 py-2 text-sm">{mv.quantidade}</td>
                <td className="px-4 py-2 text-sm">{mv.origemEmpresaNome ?? mv.origemLocalNome ?? mv.origemLocalId ?? '-'}</td>
                <td className="px-4 py-2 text-sm">{mv.destinoEmpresaNome ?? mv.destinoLocalNome ?? mv.destinoLocalId ?? '-'}</td>
                <td className="px-4 py-2 text-sm">{formatDateTime(mv.dataMovimento || mv.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      <div className="flex items-center gap-2 justify-end">
        <button disabled={page <= 0} onClick={() => setPage((p) => Math.max(p - 1, 0))} className="px-3 py-1 border rounded disabled:opacity-50">Anterior</button>
        <span className="text-sm text-gray-600">Página {page + 1} de {listData?.totalPages ?? 0}</span>
        <button disabled={(page + 1) >= (listData?.totalPages ?? 0)} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Próxima</button>
        <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="px-2 py-1 border rounded">
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
      </div>

      {/* Modal de Registro de Movimentação */}
      {openModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setOpenModal(false)}></div>
          <div role="dialog" aria-modal="true" className="relative bg-white rounded border shadow-lg w-full max-w-xl sm:max-w-2xl p-0 flex flex-col max-h-[90vh] min-h-0 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b">
              <ArrowLeftRight className="h-5 w-5 text-red-600" />
              <h2 className="text-lg font-semibold">Registrar Movimentação</h2>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 overscroll-contain">
              <StockMovementForm
                defaultProdutoId={filterProdutoId}
                defaultLocalId={filterLocalId}
                lockProduto
                onCancel={() => setOpenModal(false)}
                onSuccess={() => { setOpenModal(false); loadMovements(); setSummaryReloadFlag(Date.now()); }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockMovementPage;
